<template>
  <v-dialog
    v-model="changePinDialog"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <v-card v-if="changeEmployee" tile class="change-pin-screen">
      <header class="change-pin-header">
        <button
          type="button"
          class="header-button"
          :disabled="buttonLoading"
          aria-label="Close change PIN screen"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </button>

        <div class="employee-identity">
          <span class="employee-avatar">{{ employeeInitials }}</span>
          <div>
            <small>Changing PIN for</small>
            <strong>{{ changeEmployee.name }}</strong>
          </div>
        </div>

        <button
          v-if="step > 1 && step < 4"
          type="button"
          class="header-button header-button--back"
          :disabled="buttonLoading"
          @click="goBack"
        >
          <v-icon small>mdi-arrow-left</v-icon>
          Back
        </button>
        <span v-else class="header-spacer"></span>
      </header>

      <main class="change-pin-content">
        <section v-if="step < 4" class="change-pin-prompt">
          <div class="step-progress" aria-label="Change PIN progress">
            <span
              v-for="item in 3"
              :key="item"
              :class="{ active: item <= step }"
            ></span>
          </div>
          <div class="step-label">Step {{ step }} of 3</div>
          <h1>{{ stepTitle }}</h1>
          <p>{{ stepDescription }}</p>

          <div class="pin-dots" aria-label="PIN digits entered">
            <span
              v-for="circle in pinCodeCircles"
              :key="circle"
              :class="{ filled: activePin.length >= circle }"
            ></span>
          </div>
        </section>

        <section v-if="step < 4" class="change-pin-keypad">
          <button
            v-for="num in numbers"
            :key="num"
            type="button"
            class="keypad-button"
            :disabled="buttonLoading"
            @pointerdown.stop.prevent="pointerMode && selectedNum(num, step)"
            @click.stop.prevent="!pointerMode && selectedNum(num, step)"
          >
            {{ num }}
          </button>

          <button
            type="button"
            class="keypad-button keypad-button--utility"
            :disabled="buttonLoading || !activePin.length"
            @click="deleteDigit"
          >
            <v-icon>mdi-backspace-outline</v-icon>
            <small>Delete</small>
          </button>
          <button
            type="button"
            class="keypad-button"
            :disabled="buttonLoading"
            @pointerdown.stop.prevent="pointerMode && selectedNum(0, step)"
            @click.stop.prevent="!pointerMode && selectedNum(0, step)"
          >
            0
          </button>
          <button
            type="button"
            class="keypad-button keypad-button--clear"
            :disabled="buttonLoading || !activePin.length"
            @click="clearActivePin"
          >
            <v-icon>mdi-close-circle-outline</v-icon>
            <small>Clear</small>
          </button>
        </section>

        <section v-else class="saving-state" aria-live="polite">
          <span class="saving-icon">
            <v-progress-circular
              :size="52"
              :width="5"
              color="#22d3ee"
              indeterminate
            ></v-progress-circular>
          </span>
          <h1>Saving your new PIN</h1>
          <p>Please wait. This will only take a moment.</p>
        </section>
      </main>
    </v-card>
  </v-dialog>
</template>


<script>
import employeeService from "@/modules/Employee/service.js";
import timeClockService from "@/modules/TimeClock/service.js";
import authService from "@/modules/Authentication/service.js";

export default {
  props: ["dialog", "changeEmployee"],
  data() {
    return {
      buttonLoading: false,
      employee: null,
      pointerMode: "PointerEvent" in window,
      pin: "",
      newPin: "",
      confirmPin: "",
      numbers: [1, 2, 3, 4, 5, 6, 7, 8, 9],
      pinCodeCircles: [1, 2, 3, 4],
      changePinDialog: false,
      step: 1,
      locationCode: null,
      deviceCode: null,
      deviceName: null,
    };
  },
  computed: {
    employeeInitials() {
      return this.changeEmployee && this.changeEmployee.name
        ? this.changeEmployee.name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part.charAt(0))
            .join("")
            .toUpperCase()
        : "";
    },
    activePin() {
      switch (this.step) {
        case 1:
          return this.pin;
        case 2:
          return this.newPin;
        default:
          return this.confirmPin;
      }
    },
    stepTitle() {
      switch (this.step) {
        case 1:
          return "Enter your current PIN";
        case 2:
          return "Choose a new PIN";
        default:
          return "Confirm your new PIN";
      }
    },
    stepDescription() {
      switch (this.step) {
        case 1:
          return "First, verify your existing 4-digit employee PIN.";
        case 2:
          return "Choose a memorable 4-digit PIN you can keep private.";
        default:
          return "Enter the same PIN again to make sure it is correct.";
      }
    },
  },
  watch: {
    async dialog(val) {
      this.locationCode = localStorage.getItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}code`
      );
      const { deviceName, deviceCode } = authService.getStoredDevice();
      this.deviceName = deviceName;
      this.deviceCode = deviceCode;
      // console.log("val", val);
      // this.employee = (
      //   await timeClockService.getEmployeeTimeClock(this.changeEmployee.id)
      // ).data;
      // console.log("test", this.employee);
      this.changePinDialog = val;
    },
    step(val) {
      if (val === 1) this.empty();
      if (val === 4) this.updatePinNumber();
    },
  },
  methods: {
    updatePinNumber() {
      this.buttonLoading = true;
      this.$Progress.start();
      employeeService
        .update(this.employee.id, { pinCode: this.newPin })
        .then(() => {
          this.$toast.success("PIN changed successfully", { timeout: 2000 });
          this.$Progress.finish();
          this.close();
        })
        .catch((err) => {
          this.$toast.error(err.data ? err.data.message : "Unable to change PIN", {
            timeout: 2000,
          });
          this.$Progress.fail();
          this.buttonLoading = false;
          this.confirmPin = "";
          this.step = 3;
        });
    },
    generateTimeClockToken() {
      let rand = function () {
        return Math.random().toString(36).substr(2);
      };

      return rand() + rand();
    },
    async getToken() {
      this.$Progress.start();
      const loginInfo = {
        id: this.changeEmployee.id,
        pin: this.pin,
        locationCode: this.locationCode,
        deviceCode: this.deviceCode,
        deviceName: this.deviceName,
      };
      try {
        await authService.authenticate(loginInfo);
        this.$Progress.finish();
        return true;
      } catch (err) {
        this.$Progress.fail();
        this.$toast.error("That PIN is not correct. Please try again.", {
          timeout: 2000,
        });
        this.empty();
        return false;
      }
    },
    async selectedNum(num, step) {
      if (this.buttonLoading || step !== this.step || this.activePin.length >= 4) {
        return;
      }
      num = num.toString();
      if (step === 1) {
        this.pin = this.pin.concat(num).trim();

        if (this.pin.length === 4) {
          this.buttonLoading = true;
          if (await this.getToken()) {
            try {
              this.employee = (
                await timeClockService.getEmployeeTimeClock(
                  this.changeEmployee.id
                )
              ).data;
              if (this.employee.pinCode === this.pin) {
                this.buttonLoading = false;
                this.step = 2;
              } else {
                this.$toast.error("That PIN is not correct. Please try again.", {
                  timeout: 2000,
                });
                this.empty();
              }
            } catch (err) {
              this.$toast.error("Unable to verify your PIN. Please try again.", {
                timeout: 2000,
              });
              this.empty();
            }
          } else {
            this.empty();
          }
        }
      }
      if (step === 2) {
        this.newPin = this.newPin.concat(num).trim();
        if (this.newPin.length === 4) {
          this.step = 3;
        }
      }
      if (step === 3) {
        this.confirmPin = this.confirmPin.concat(num).trim();
        if (this.confirmPin.length === 4) {
          if (this.confirmPin === this.newPin) {
            this.step = 4;
          } else {
            this.$toast.error("The PINs did not match. Please try again.", {
              timeout: 2000,
            });
            this.confirmPin = "";
            this.newPin = "";
            this.step = 2;
          }
        }
      }
    },
    deleteDigit() {
      if (this.buttonLoading) return;
      if (this.step === 1) this.pin = this.pin.slice(0, -1);
      if (this.step === 2) this.newPin = this.newPin.slice(0, -1);
      if (this.step === 3) this.confirmPin = this.confirmPin.slice(0, -1);
    },
    clearActivePin() {
      if (this.buttonLoading) return;
      if (this.step === 1) this.pin = "";
      if (this.step === 2) this.newPin = "";
      if (this.step === 3) this.confirmPin = "";
    },
    goBack() {
      if (this.buttonLoading || this.step <= 1) return;
      if (this.step === 3) {
        this.confirmPin = "";
        this.step = 2;
        return;
      }
      this.newPin = "";
      this.pin = "";
      this.step = 1;
    },
    close() {
      this.$emit("close");
      this.empty();
    },
    empty() {
      this.step = 1;
      this.pin = "";
      this.newPin = "";
      this.confirmPin = "";
      this.employee = null;
      this.buttonLoading = false;
    },
  },
};
</script>

<style scoped>
.change-pin-screen {
  min-height: 100vh;
  color: #e2e8f0;
  background-color: #07111f !important;
  background-image: linear-gradient(
      rgba(34, 211, 238, 0.035) 1px,
      transparent 1px
    ),
    linear-gradient(
      90deg,
      rgba(34, 211, 238, 0.035) 1px,
      transparent 1px
    ),
    radial-gradient(circle at 50% 0%, rgba(30, 64, 175, 0.22), transparent 48%);
  background-size: 72px 72px, 72px 72px, auto;
}

.change-pin-header {
  display: grid;
  min-height: 78px;
  padding: 10px 20px;
  grid-template-columns: minmax(80px, 1fr) auto minmax(80px, 1fr);
  align-items: center;
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
  background: rgba(7, 17, 31, 0.9);
}

.employee-identity {
  display: flex;
  align-items: center;
  gap: 12px;
}

.employee-identity small,
.employee-identity strong {
  display: block;
}

.employee-identity small {
  color: #64748b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.3px;
  text-transform: uppercase;
}

.employee-identity strong {
  color: #f8fafc;
  font-size: 17px;
}

.employee-avatar {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 1px solid rgba(34, 211, 238, 0.2);
  border-radius: 13px;
  background: rgba(34, 211, 238, 0.1);
  color: #67e8f9;
  font-weight: 800;
}

.header-button {
  display: inline-flex;
  width: 48px;
  height: 48px;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.05);
  color: #cbd5e1;
  touch-action: manipulation;
}

.header-button .v-icon {
  color: inherit;
}

.header-button--back {
  width: auto;
  min-width: 88px;
  margin-left: auto;
  padding: 0 14px;
  gap: 7px;
  font-weight: 700;
}

.header-button:active,
.keypad-button:active {
  transform: scale(0.97);
}

.header-button:disabled,
.keypad-button:disabled {
  opacity: 0.4;
}

.header-spacer {
  width: 80px;
}

.change-pin-content {
  display: flex;
  min-height: calc(100vh - 78px);
  padding: clamp(22px, 4vh, 42px) 20px;
  flex-direction: column;
  align-items: center;
}

.change-pin-prompt {
  width: 100%;
  max-width: 660px;
  text-align: center;
}

.step-progress {
  display: grid;
  max-width: 260px;
  margin: 0 auto 12px;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.step-progress span {
  height: 4px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.18);
}

.step-progress span.active {
  background: linear-gradient(90deg, #2563eb, #22d3ee);
}

.step-label {
  color: #67e8f9;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.4px;
  text-transform: uppercase;
}

.change-pin-prompt h1 {
  margin: 8px 0 6px;
  color: #f8fafc;
  font-size: clamp(28px, 4.2vw, 42px);
  font-weight: 800;
  letter-spacing: -1px;
}

.change-pin-prompt p {
  margin: 0;
  color: #94a3b8;
  font-size: 14px;
}

.pin-dots {
  display: flex;
  margin: 20px 0 22px;
  justify-content: center;
  gap: 16px;
}

.pin-dots span {
  width: 24px;
  height: 24px;
  border: 2px solid #64748b;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.8);
  transition: 120ms ease;
}

.pin-dots span.filled {
  border-color: #22d3ee;
  background: #22d3ee;
  box-shadow: 0 0 0 5px rgba(34, 211, 238, 0.12);
}

.change-pin-keypad {
  display: grid;
  width: min(100%, 600px);
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(10px, 1.8vh, 16px);
}

.keypad-button {
  display: flex;
  min-height: clamp(76px, 11vh, 108px);
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 22px;
  background: rgba(17, 32, 51, 0.94);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.18);
  color: #f8fafc;
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 750;
  transition: 100ms ease;
  touch-action: manipulation;
  user-select: none;
}

.keypad-button--utility,
.keypad-button--clear {
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}

.keypad-button--utility {
  background: rgba(30, 41, 59, 0.88);
  color: #cbd5e1;
}

.keypad-button--clear {
  border-color: rgba(251, 113, 133, 0.28);
  background: rgba(244, 63, 94, 0.11);
  color: #fda4af;
}

.keypad-button--utility .v-icon,
.keypad-button--clear .v-icon {
  color: inherit;
  font-size: 27px;
}

.keypad-button small {
  color: inherit;
  font-size: 12px;
  font-weight: 750;
}

.saving-state {
  display: flex;
  min-height: 60vh;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.saving-icon {
  display: grid;
  width: 92px;
  height: 92px;
  margin-bottom: 24px;
  place-items: center;
  border: 1px solid rgba(34, 211, 238, 0.2);
  border-radius: 26px;
  background: rgba(34, 211, 238, 0.08);
}

.saving-state h1 {
  margin: 0 0 8px;
  color: #f8fafc;
  font-size: clamp(30px, 5vw, 44px);
}

.saving-state p {
  margin: 0;
  color: #94a3b8;
}

@media (max-width: 600px) {
  .change-pin-header {
    padding: 10px 12px;
    grid-template-columns: 60px auto 72px;
  }

  .employee-avatar {
    display: none;
  }

  .employee-identity strong {
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header-button--back {
    min-width: 70px;
    padding: 0 9px;
  }
}

@media (max-height: 760px) {
  .change-pin-content {
    padding-top: 16px;
  }

  .change-pin-prompt p {
    display: none;
  }

  .pin-dots {
    margin: 13px 0 14px;
  }

  .keypad-button {
    min-height: 68px;
  }
}
</style>
